import React, { Component } from "react";
import PropTypes from "prop-types";
import MaterialTable from "material-table";
import { Box } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { fetch } from "whatwg-fetch";
import visionCallService from "../../services/visionCallService";
import ColumnHeader from "./ColumnHeader";
import Pagination from "./Pagination";
import FilterRow from "./FilterRow";
import CircularProgressOverlay from "./CircularProgressOverlay";
import BodyRow from "./BodyRow";
import Body from "./Body";
import { omit } from "lodash";

const styles = theme => ({
  container: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    "& > div": {
      // borderBottom: "solid 1px rgb(224, 224, 224)"
    },
    "& > div > div > div": {
      overflowY: "unset !important" // override material table inline styling
    }
  }
});

class Table extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRows: {},
      selectAllChecked: false,
      totalSelectableRows: 0,
      totalRows: 0
    };
    this.fetchData = this.fetchData.bind(this);
  }

  updateSelectAllCheckbox = () => {
    this.setState({
      selectAllChecked:
        this.state.totalSelectableRows > 0 &&
        Object.keys(this.state.selectedRows).length ===
          this.state.totalSelectableRows
    });
  };

  handleSelectionChange = (data, rowData) => {
    const { onSelectionChange, getRowId } = this.props;
    const rowId = getRowId(rowData);
    let selectedRows = this.state.selectedRows;
    if (this.props.rowSelectionDisabled(rowData)) return;
    if (selectedRows[rowId]) {
      selectedRows = omit(selectedRows, [rowId]);
    } else if (!selectedRows[rowId]) {
      selectedRows[rowId] = true;
    }
    this.setState({ selectedRows }, () => {
      if (onSelectionChange) {
        onSelectionChange(selectedRows);
      }
      this.updateSelectAllCheckbox();
    });
  };

  /**
   * When the select all checkbox is clicked, loop through rows to set
   * the selected rows if the row has a checkbox
   */
  handleAllSelected = selectAllChecked => {
    const {
      showRowSelection,
      onSelectionChange,
      tableRef,
      getRowId
    } = this.props;
    const selectedRows = {};
    if (selectAllChecked) {
      tableRef.current.dataManager.data.forEach(rowData => {
        if (this.props.rowSelectionDisabled(rowData)) return;
        selectedRows[getRowId(rowData)] = showRowSelection
          ? showRowSelection(rowData)
          : true;
      });
    }
    this.setState({ selectAllChecked });
    this.setState({ selectedRows }, () =>
      onSelectionChange ? onSelectionChange(selectedRows) : null
    );
  };

  showRowSelection(rowData) {
    if (this.props.showRowSelection) {
      return this.props.showRowSelection(rowData);
    } else {
      return true;
    }
  }

  componentDidUpdate() {
    const { selectedRows, resetFilters } = this.props;
    if (selectedRows && selectedRows !== this.state.selectedRows) {
      this.setState({ selectedRows }, () => this.updateSelectAllCheckbox()); // allows us to control selected rows from parent component
    }
    const totalRows =
      this.props.tableRef && this.props.tableRef.current
        ? this.props.tableRef.current.dataManager.data.length
        : 0;

    if (totalRows !== this.state.totalRows) {
      this.setState({ totalRows }, () =>
        this.setState({
          totalSelectableRows: this.props.showRowSelection
            ? this.props.tableRef.current.dataManager.data.reduce(
                (total, rowData) =>
                  (total += this.props.showRowSelection(rowData) ? 1 : 0),
                0
              )
            : this.state.totalRows
        })
      );
    }
    if (resetFilters) {
      this.handleAllSelected(false);
    }
  }

  fetchData(query) {
    const { remoteSource } = this.props;
    let url = `${remoteSource}&current_page=${query.page + 1}&per_page=${
      query.pageSize
    }`;

    query.filters.forEach(filter => {
      url += `&filter[${filter.column.field}]=${filter.value}`;
    });

    if (query.orderBy) {
      const columnName = query.orderBy.field;
      url += `&sort[column]=${columnName}&sort[direction]=${query.orderDirection}`;
    }
    return fetch(url, { credentials: "include" })
      .then(response => {
        visionCallService.redirectForAuth(response);
        return response.json();
      })
      .then(function(response) {
        return {
          data: response.data.map(item => item.attributes),
          totalCount: response.meta.total_count,
          page: response.meta.current_page - 1
        };
      })
      .catch(function(error) {
        return {
          data: []
        };
      });
  }

  render() {
    const {
      columns,
      data,
      filtering,
      selection,
      rowsPerPageOptions,
      rowStyle,
      hideDetailButton,
      detailPanel,
      onRowClick,
      selectionCellStyle,
      selectAllStyle,
      rowClasses,
      remoteSource,
      hidePagination,
      selectionProps,
      padding,
      itemType,
      totalRows,
      rowClickDisabled,
      tableRef,
      isDraggable,
      tableLayout,
      detailPanelColumnAlignment,
      onDragEnd,
      maxBodyHeight,
      containerClass,
      isLoading,
      defaultPerPage,
      resetFilters,
      classes,
      getRowId
    } = this.props;
    return (
      <MaterialTable
        tableRef={tableRef}
        options={{
          padding,
          toolbar: false,
          filtering,
          selection,
          paging: !hidePagination,
          pageSize: defaultPerPage || rowsPerPageOptions[0],
          pageSizeOptions: rowsPerPageOptions,
          emptyRowsWhenPaging: false,
          maxBodyHeight,
          detailPanelType: "single",
          rowStyle,
          rowClasses,
          hideDetailButton,
          selectionCellStyle,
          selectionProps,
          tableLayout,
          detailPanelColumnAlignment,
          debounceInterval: 600
        }}
        onSelectionChange={this.handleSelectionChange}
        columns={columns}
        data={
          remoteSource
            ? this.fetchData
            : typeof data === "function"
            ? props => data({ ...props, page: props.page + 1 })
            : data
        }
        components={{
          Pagination: props => {
            return !hidePagination ? (
              <Pagination
                {...props}
                itemType={itemType}
                totalRows={totalRows}
              />
            ) : null;
          },
          Header: props => {
            return (
              <ColumnHeader
                {...props}
                onAllSelected={this.handleAllSelected}
                selectAllChecked={this.state.selectAllChecked}
                selectionCellStyle={selectionCellStyle}
                selectAllStyle={selectAllStyle}
                isDraggable={isDraggable}
                detailPanel={detailPanel}
                isLoading={tableRef.current && tableRef.current.state.isLoading}
                isResetFilters={resetFilters}
              />
            );
          },
          Body: props => (
            <Body
              {...props}
              isDraggable={isDraggable}
              onDragEnd={onDragEnd}
              selection={selection}
            />
          ),
          Container: props => (
            <Box
              {...props}
              className={`${classes.container} ${containerClass}`}
            />
          ),
          FilterRow: props => (
            <FilterRow {...props} isResetFilters={resetFilters} />
          ),
          OverlayLoading: props => <CircularProgressOverlay {...props} />,
          Row: props => (
            <BodyRow
              {...props}
              data={{
                ...props.data,
                tableData: {
                  ...props.data.tableData,
                  checked: this.state.selectedRows[getRowId(props.data)]
                }
              }}
              showSelection={this.showRowSelection(props.data)}
              rowClickDisabled={rowClickDisabled}
              isDraggable={isDraggable}
            />
          )
        }}
        detailPanel={detailPanel}
        onRowClick={onRowClick}
        className={classes.body}
        data-qa="table"
        isLoading={isLoading}
      />
    );
  }
}

export default withStyles(styles)(Table);
Table.propTypes = {
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  columns: PropTypes.arrayOf(PropTypes.any),
  remoteSource: PropTypes.string,
  padding: PropTypes.string,
  filtering: PropTypes.bool,
  selection: PropTypes.bool,
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.func
  ]),
  detailPanel: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.func]))
  ]),
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  hideDetailButton: PropTypes.bool,
  rowStyle: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  selectionCellStyle: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  rowClasses: PropTypes.object,
  hidePagination: PropTypes.bool,
  itemType: PropTypes.string,
  totalRows: PropTypes.number,
  rowClickDisabled: PropTypes.func,
  rowSelectionDisabled: PropTypes.func,
  tableLayout: PropTypes.oneOf(["auto", "fixed"]),
  detailPanelColumnAlignment: PropTypes.string,
  /**
   * Determins if rows are draggable
   */
  isDraggable: PropTypes.bool,
  onDragEnd: PropTypes.func,
  maxBodyHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool
};

Table.defaultProps = {
  filtering: true,
  rowsPerPageOptions: [50, 100, 200],
  columns: [],
  remoteSource: null,
  data: [],
  selection: false,
  hideDetailButton: false,
  padding: "default",
  tableRef: React.createRef(null),
  tableLayout: "auto",
  maxBodyHeight: 522,
  detailPanelColumnAlignment: "right",
  rowSelectionDisabled: row => false,
  getRowId: row => row.id
};
